import clsx from 'clsx';
import React, { useCallback, useEffect, useRef } from 'react';

import TopNavItem from '@components/Nav/TopNav/NavItem';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import { useSectionRegistryState } from './SectionRegistryContext';
import { MY_TI_NAV_ITEMS } from './constants';

const SCROLL_DELAY_MS = 100;
const MAX_FIT_VISIBLE_NAV_ITEMS = 6;

const MyTINav = () => {
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.Nav,
  });
  const registeredSections = useSectionRegistryState();
  const hasScrolledRef = useRef(false);

  const visibleNavItems = MY_TI_NAV_ITEMS.filter((item) => registeredSections.has(item.id));

  const scrollToSection = useCallback((sectionId: string) => {
    const target = document.getElementById(sectionId);
    if (!target) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }, []);

  const handleNavClick = useCallback(
    (sectionId: string) => {
      scrollToSection(sectionId);
      window.history.pushState(null, '', `#${sectionId}`);
      trackEvent(click({ clickTarget: ClickTarget.NavLink, clickText: sectionId }));
    },
    [scrollToSection, trackEvent]
  );

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (!hasScrolledRef.current && hash && registeredSections.has(hash)) {
      const timeout = setTimeout(() => {
        scrollToSection(hash);
        hasScrolledRef.current = true;
      }, SCROLL_DELAY_MS);
      return () => clearTimeout(timeout);
    }
  }, [registeredSections, scrollToSection]);

  const visibleNavItemsLength = visibleNavItems.length;

  if (visibleNavItemsLength === 0) return null;

  return (
    <nav
      aria-label="Page sections"
      className="mt-48 border-0 border-b border-t border-solid border-grayscale-beige py-32 [&+_article]:mt-0 [&_+_article]:border-t-0 [&_+_article]:pt-48 [&_+_article]:sm:pt-80"
    >
      <ul
        className={clsx(
          'm-0 flex flex-1 list-none flex-col items-center justify-center p-0',
          visibleNavItemsLength > MAX_FIT_VISIBLE_NAV_ITEMS
            ? 'md:flex-row md:gap-24'
            : 'sm:flex-row sm:gap-24'
        )}
      >
        {visibleNavItems.map((item, index) => (
          <TopNavItem
            key={item.id}
            as="button"
            type="button"
            className={clsx(
              'appearance-none before:hidden',
              visibleNavItemsLength > MAX_FIT_VISIBLE_NAV_ITEMS
                ? 'md:before:block'
                : 'sm:before:block'
            )}
            showLeftDivider={index > 0}
            onClick={() => handleNavClick(item.id)}
          >
            {item.label}
          </TopNavItem>
        ))}
      </ul>
    </nav>
  );
};

export default MyTINav;
